<template>
    <div class="container tile">
        <LoadingPlaceholder v-if="loading" />
        <div v-if="!loading" class="col-sm-6 mx-auto">
            <h1>Multi-factor Authentication Settings</h1>
            <div class="card">
                <div class="card-body">
                    <h2 class="card-title h5">{{ title }}</h2>
                    <p class="card-text">{{ description }}</p>
                </div>
                <ul class="list-group list-group-flush">
                    <li class="list-group-item">
                        <p class="card-text">{{ inputType }}</p>
                        <form @submit.prevent="submit">
                            <div class="row">
                                <div class="col-lg-9 mb-2">
                                    <label class="sr-only" for="updateFactorInput">Update the multi-factor authentication value</label>
                                    <input type="text" v-model="updateFactorInput" id="updateFactorInput" class="form-control" v-on:change="inputFormatValid" :placeholder="inputType" required />
                                    <span style="color: red" v-show="emailErr">{{ inputType }} format is incorrect</span>
                                </div>
                                <div class="col-sm-3">
                                    <input type="submit" value="Update" id="updateFactor" class="btn btn-primary" />
                                </div>
                            </div>
                        </form>
                    </li>
                </ul>
                <div v-show="saveStatus == 'fail'" class="alert alert-danger mt-3">{{ saveErrors }}</div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, inject, onActivated } from "vue";
import LoadingPlaceholder from "@/lib/LoadingPlaceholder.3.vue";
import generateToken from "../../../lib/generateToken";
import getData from "@/admin/lib/getData";
import { useMainStore } from "@/mainStore";
import { changePageInjectKey } from "@/AppShell.3.vue.InjectKeys";
interface Props {
    settings: {
        factorType: string | number;
    };
}
export default defineComponent({
    components: {
        LoadingPlaceholder,
    },
    props: ["settings"],
    setup(props: Props) {
        const mainStore = useMainStore();
        const loading = ref(true);
        const factorType = ref("" as string | number);
        const tokenType = ref("");
        const updateFactorInput = ref("");
        const saveStatus = ref(null as null | "in-progress" | "ok" | "fail");
        const saveErrors = ref([] as string[]);
        const description = ref("");
        const title = ref("");
        const emailErr = ref(false);
        const inputType = computed(() => {
            if (factorType.value == "4" || factorType.value == "Email") {
                return "Email";
            } else {
                return "Phone Number";
            }
        });
        const changePage = inject(changePageInjectKey)!;
        const submit = async () => {
            saveStatus.value = "in-progress";
            if (emailErr.value) {
                return false;
            }
            try {
                const json = await generateToken(mainStore, () => mainStore.getUrl("multiFactorAuthenticationGenerateToken"), tokenType.value, updateFactorInput.value);
                if (json.Success) {
                    saveStatus.value = "ok";
                    changePage({
                        page: "MultiFactorAuthenticationVerifyToken",
                        settings: { tokenType: tokenType.value, factorType: factorType.value },
                    });
                } else {
                    saveStatus.value = "fail";
                    saveErrors.value = json.Errors || ["Unknown error occurred"];
                }
            } catch {
                saveStatus.value = "fail";
                saveErrors.value = ["Unknown error occurred"];
            }
        };
        const init = async () => {
            loading.value = true;
            var loadingFactorType: number | string = "None";
            if (props.settings != null) {
                loadingFactorType = props.settings.factorType;
            }
            const vm = await getData(mainStore, "WisconsinDefault", () => mainStore.getUrl("multiFactorAuthenticationEditData", { factorType: loadingFactorType }));
            if (vm != null) {
                factorType.value = vm.FactorType;
                tokenType.value = vm.TokenType;
                description.value = vm.Description;
                title.value = vm.Title;
            }
            updateFactorInput.value = "";
            emailErr.value = false;
            loading.value = false;
        };
        const inputFormatValid = () => {
            const reg = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
            const regPhone = /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
            if (factorType.value == 4 && reg.test(updateFactorInput.value)) {
                emailErr.value = false;
            } else if (factorType.value == 1 && regPhone.test(updateFactorInput.value)) {
                emailErr.value = false;
            } else {
                emailErr.value = true;
            }
        };
        onActivated(() => {
            init();
        });

        const result = {
            loading,
            title,
            description,
            inputType,
            submit,
            updateFactorInput,
            inputFormatValid,
            emailErr,
            saveStatus,
            saveErrors,
        };
        ("#if JEST");
        //@ts-ignore
        result.init = init;
        //@ts-ignore
        result.factorType = factorType;
        ("#endif");
        return result;
    },
});
</script>
